import {
  Button,
  Col,
  DatePicker,
  Empty,
  Form,
  Row,
  Select,
  Space,
  Spin,
  TreeSelect,
  Typography,
  message,
} from "antd";
import { DownloadOutlined, RightOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs, { type Dayjs } from "dayjs";
import styles from "./Reports.module.scss";
import { DebounceSelect, PermissionDirect } from "../../Components";
import { useRecoilValue } from "recoil";
import {
  FreightRevenueApi,
  ReportsApi,
  RouteManagerApi,
  TruckVendorApi,
  VehicleApi,
  getPermissionRegions,
} from "../../services";
import { UserAtom } from "../../store";
import { checkPermissions } from "../../utils/checkPermissions";
import { getTruckVendorTypeByOwnership } from "../../shared/helpers/truckVendorOwnership";
import {
  buildReportExportParams,
  getFirstAvailableReport,
  getReportPeriodOptions,
  getReportPeriodRange,
  getVehicleOptionsFromTruckVendor,
  hasReportFilter,
  isReportAvailable,
  mapPermissionRegionsToTreeData,
  REPORT_ORDER_STATUS_OPTIONS,
  shouldDisableReportTruckVendor,
  type ReportConfig,
  type ReportOption,
  type ReportPeriodMode,
} from "./reportFilters";

const { Text } = Typography;

const REPORT_OWNERSHIP_OPTIONS = [
  { label: "Đầu tư", value: "INVESTMENT" },
  { label: "Thuê khô", value: "DRY_RENTAL" },
  { label: "Dịch vụ", value: "SERVICE" },
];

const REPORT_TYPE_OPTIONS = [
  { label: "Kế hoạch", value: "ESTIMATED" },
  { label: "Thực tế", value: "ACTUAL" },
];

const REPORT_DIRECTION_OPTIONS = [
  { label: "HP-DN", value: "HP-DN" },
  { label: "HP-SG", value: "HP-SG" },
  { label: "SG-DN", value: "SG-DN" },
  { label: "SG-HP", value: "SG-HP" },
  { label: "DN-SG", value: "DN-SG" },
  { label: "DN-HP", value: "DN-HP" },
];

const REPORT_CONTAINER_TYPE_OPTIONS = [20, 40, 45, 48].map((item) => ({
  label: String(item),
  value: item,
}));

const fieldLabels: Record<string, string> = {
  orderStagePeriod: "Kỳ ghi nhận",
  region: "Khu vực",
  ownership: "Hình thức sở hữu",
  direction: "Chiều hàng",
  truckVendor: "Nhà thầu",
  customer: "Khách hàng",
  containerType: "Phân loại container",
  routeBase: "Mã tuyến",
  vehicle: "Xe",
  type: "Loại dữ liệu",
  status: "Trạng thái đơn hàng",
};

const quickRanges = [
  {
    label: "Tháng này",
    getRange: (): [Dayjs, Dayjs] => [
      dayjs().startOf("month"),
      dayjs().endOf("month"),
    ],
  },
  {
    label: "Quý này",
    getRange: (): [Dayjs, Dayjs] => {
      const quarterStartMonth = Math.floor(dayjs().month() / 3) * 3;
      const start = dayjs().month(quarterStartMonth).startOf("month");
      return [start, start.add(2, "month").endOf("month")];
    },
  },
  {
    label: "Năm này",
    getRange: (): [Dayjs, Dayjs] => [
      dayjs().startOf("year"),
      dayjs().endOf("year"),
    ],
  },
  {
    label: "Tháng trước",
    getRange: (): [Dayjs, Dayjs] => [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
  {
    label: "Quý trước",
    getRange: (): [Dayjs, Dayjs] => {
      const base = dayjs().subtract(3, "month");
      const quarterStartMonth = Math.floor(base.month() / 3) * 3;
      const start = base.month(quarterStartMonth).startOf("month");
      return [start, start.add(2, "month").endOf("month")];
    },
  },
  {
    label: "Năm trước",
    getRange: (): [Dayjs, Dayjs] => [
      dayjs().subtract(1, "year").startOf("year"),
      dayjs().subtract(1, "year").endOf("year"),
    ],
  },
];

const toReportOption = (item: any, labelKey = "name"): ReportOption => ({
  label:
    item?.[labelKey] ||
    item?.shortName ||
    item?.name ||
    item?.code ||
    item?.licensePlate ||
    "",
  value: item?._id ?? item?.value,
  _id: item?._id,
  vehicleList: item?.vehicleList ?? [],
});

const getReportFilename = (report: ReportConfig) => `${report.name}.xlsx`;

const Reports = () => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const permissionState = useRecoilValue<any>(UserAtom.permissions);
  const features = permissionState?.features;
  const canExportReport = checkPermissions("REPORT_SHEET", "export", features);
  const [reportConfigs, setReportConfigs] = useState<ReportConfig[]>([]);
  const [activeReportCode, setActiveReportCode] = useState<string>();
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [regionOptions, setRegionOptions] = useState<any[]>([]);
  const [vehicleOptions, setVehicleOptions] = useState<ReportOption[]>([]);
  const [periodMode, setPeriodMode] = useState<ReportPeriodMode>("month");

  const ownership = Form.useWatch("ownership", form);
  const truckVendor = Form.useWatch("truckVendor", form);
  const activeReport = reportConfigs.find(
    (report) => report.code === activeReportCode,
  );
  const activeFilterKeys = activeReport?.filterKeys ?? [];
  const hasTimeRange =
    activeFilterKeys.includes("from") || activeFilterKeys.includes("to");
  const periodOptions = useMemo(() => getReportPeriodOptions(dayjs()), []);

  const fetchReportConfig = useCallback(async () => {
    try {
      setLoadingConfig(true);
      const response = await ReportsApi.getAllReportConfig();
      const configs = response?.data ?? [];
      const firstAvailableReport = getFirstAvailableReport(configs);

      setReportConfigs(configs);
      setActiveReportCode((currentCode) => {
        const currentReport = configs.find(
          (report: ReportConfig) => report.code === currentCode,
        );

        return isReportAvailable(currentReport)
          ? currentCode
          : firstAvailableReport?.code;
      });
    } catch (error: any) {
      messageApi.error(error?.message || "Không tải được cấu hình báo cáo");
    } finally {
      setLoadingConfig(false);
    }
  }, [messageApi]);

  useEffect(() => {
    fetchReportConfig();
  }, [fetchReportConfig]);

  useEffect(() => {
    getPermissionRegions({}).then((regions) => {
      setRegionOptions(mapPermissionRegionsToTreeData(regions ?? []));
    });
  }, []);

  useEffect(() => {
    form.resetFields();
    setVehicleOptions([]);
  }, [activeReportCode, form]);

  const handleSetRange = (range: [Dayjs, Dayjs]) => {
    form.setFieldsValue({
      from: range[0],
      to: range[1],
    });
  };
  const handleChoosePeriod = (value: number) => {
    handleSetRange(getReportPeriodRange(periodMode, value));
  };

  const fetchCustomerOptions = (search: string) =>
    FreightRevenueApi.getAllCustomer({ searchText: search }).then((res: any) =>
      (res?.data ?? []).map((item: any) => toReportOption(item)),
    );

  const fetchRouteBaseOptions = (search: string) =>
    RouteManagerApi.getAllRouterBase(
      { searchText: search },
      { silentLoading: true },
    ).then((res: any) =>
      (res?.data ?? []).map((item: any) => toReportOption(item, "code")),
    );

  const fetchTruckVendorOptions = useCallback(
    (search: string) => {
      const values = {
        ...form.getFieldsValue(),
        ownership,
      };

      if (shouldDisableReportTruckVendor(activeFilterKeys, values)) {
        return Promise.resolve([]);
      }

      return TruckVendorApi.getAllTruckVendorSearch({
        searchText: search,
        truckVendorType: activeFilterKeys.includes("ownership")
          ? getTruckVendorTypeByOwnership(values.ownership)
          : "",
      }).then((res: any) =>
        (res?.data ?? []).map((item: any) => toReportOption(item, "shortName")),
      );
    },
    [activeFilterKeys, form, ownership],
  );

  const fetchVehicleOptions = (search: string) =>
    VehicleApi.getAllVehicleV2({
      current: 1,
      pageSize: 20,
      searchText: search,
    }).then((res: any) =>
      (res?.data ?? []).map((item: any) => ({
        label: item.licensePlate,
        value: item._id,
      })),
    );

  const handleOwnershipChange = () => {
    form.setFieldsValue({
      truckVendor: undefined,
      vehicle: undefined,
    });
    setVehicleOptions([]);
  };

  const handleTruckVendorChange = (value: any, option: any) => {
    form.setFieldsValue({ vehicle: undefined });
    setVehicleOptions(getVehicleOptionsFromTruckVendor(option ?? value));
  };

  const handleExport = async () => {
    if (!activeReport || !isReportAvailable(activeReport) || !canExportReport) return;

    try {
      const values = await form.validateFields();
      const params = buildReportExportParams({
        filterKeys: activeReport.filterKeys,
        values,
      });

      setExporting(true);
      await ReportsApi.exportReportByConfig({
        urlPath: activeReport.urlPath,
        params,
        fallbackFilename: getReportFilename(activeReport),
      });
    } catch (error: any) {
      if (!error?.errorFields) {
        messageApi.error(error?.message || "Xuất báo cáo lỗi");
      }
    } finally {
      setExporting(false);
    }
  };

  const renderTimeFilters = () => {
    if (!hasTimeRange) return null;

    return (
      <div className={styles.TimeCard}>
        <Text strong className={styles.TimeTitle}>Thời gian</Text>
        <div className={styles.TimeBody}>
          <div className={styles.TimeLeft}>
            <div className={styles.DateRow}>
              {hasReportFilter(activeReport, "from") && (
                <Form.Item name="from" className={styles.DateItem}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    placeholder="Từ"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}
              {hasReportFilter(activeReport, "to") && (
                <Form.Item name="to" className={styles.DateItem}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    placeholder="Đến"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}
            </div>
            <Space wrap className={styles.QuickRanges}>
              {quickRanges.map((item) => (
                <Button key={item.label} onClick={() => handleSetRange(item.getRange())}>
                  {item.label}
                </Button>
              ))}
            </Space>
          </div>
          <div className={styles.TimeRight}>
            <div className={styles.PeriodPanel}>
              <Space wrap className={styles.ModeButtons}>
                {[
                  { label: "Theo tháng", value: "month" as const },
                  { label: "Theo Quý", value: "quarter" as const },
                  { label: "Theo năm", value: "year" as const },
                ].map((item) => (
                  <Button
                    key={item.value}
                    className={
                      periodMode === item.value
                        ? styles.ModeButtonActive
                        : styles.ModeOutlineButton
                    }
                    onClick={() => setPeriodMode(item.value)}
                  >
                    {item.label}
                  </Button>
                ))}
              </Space>
              <div className={styles.MonthGrid}>
                {periodOptions[periodMode].map((item) => (
                  <Button
                    key={`${periodMode}-${item.value}`}
                    className={styles.MonthButton}
                    onClick={() => handleChoosePeriod(item.value)}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFilterField = (filterKey: string) => {
    switch (filterKey) {
      case "orderStagePeriod":
        return (
          <Form.Item name="orderStagePeriod" label={fieldLabels.orderStagePeriod}>
            <DatePicker
              picker="month"
              format="YYYY-MM"
              placeholder="Chọn kỳ ghi nhận"
              style={{ width: "100%" }}
            />
          </Form.Item>
        );
      case "region":
        return (
          <Form.Item name="region" label={fieldLabels.region}>
            <TreeSelect
              allowClear
              showSearch
              treeData={regionOptions}
              treeDefaultExpandAll
              placeholder="Chọn khu vực"
              style={{ width: "100%" }}
            />
          </Form.Item>
        );
      case "ownership":
        return (
          <Form.Item name="ownership" label={fieldLabels.ownership}>
            <Select
              allowClear
              placeholder="Chọn hình thức sở hữu"
              options={REPORT_OWNERSHIP_OPTIONS}
              onChange={handleOwnershipChange}
            />
          </Form.Item>
        );
      case "direction":
        return (
          <Form.Item name="direction" label={fieldLabels.direction}>
            <Select
              allowClear
              placeholder="Chọn chiều hàng"
              options={REPORT_DIRECTION_OPTIONS}
            />
          </Form.Item>
        );
      case "truckVendor":
        return (
          <Form.Item name="truckVendor" label={fieldLabels.truckVendor}>
            <DebounceSelect
              allowClear
              showSearch
              placeholder="Chọn nhà thầu"
              fetchOptions={fetchTruckVendorOptions}
              disabled={shouldDisableReportTruckVendor(activeFilterKeys, {
                ownership,
              })}
              onChange={handleTruckVendorChange}
            />
          </Form.Item>
        );
      case "customer":
        return (
          <Form.Item name="customer" label={fieldLabels.customer}>
            <DebounceSelect
              allowClear
              showSearch
              placeholder="Chọn khách hàng"
              fetchOptions={fetchCustomerOptions}
            />
          </Form.Item>
        );
      case "containerType":
        return (
          <Form.Item name="containerType" label={fieldLabels.containerType}>
            <Select
              allowClear
              placeholder="Chọn loại container"
              options={REPORT_CONTAINER_TYPE_OPTIONS}
            />
          </Form.Item>
        );
      case "routeBase":
        return (
          <Form.Item name="routeBase" label={fieldLabels.routeBase}>
            <DebounceSelect
              allowClear
              showSearch
              placeholder="Chọn mã tuyến"
              fetchOptions={fetchRouteBaseOptions}
            />
          </Form.Item>
        );
      case "vehicle":
        return (
          <Form.Item name="vehicle" label={fieldLabels.vehicle}>
            {activeFilterKeys.includes("truckVendor") ? (
              <Select
                allowClear
                showSearch
                placeholder="Chọn xe"
                options={vehicleOptions}
                disabled={!truckVendor}
              />
            ) : (
              <DebounceSelect
                allowClear
                showSearch
                placeholder="Chọn xe"
                fetchOptions={fetchVehicleOptions}
              />
            )}
          </Form.Item>
        );
      case "type":
        return (
          <Form.Item name="type" label={fieldLabels.type}>
            <Select
              allowClear
              mode="multiple"
              placeholder="Chọn loại dữ liệu"
              options={REPORT_TYPE_OPTIONS}
            />
          </Form.Item>
        );
      case "status":
        return (
          <Form.Item name="status" label={fieldLabels.status}>
            <Select
              allowClear
              placeholder="Chọn trạng thái đơn hàng"
              options={REPORT_ORDER_STATUS_OPTIONS}
            />
          </Form.Item>
        );
      default:
        return null;
    }
  };

  const renderFilterGrid = () => {
    if (!activeReport) {
      return <Empty description="Không có báo cáo khả dụng" />;
    }

    const filterKeys = activeReport.filterKeys.filter(
      (filterKey) => filterKey !== "from" && filterKey !== "to",
    );

    return (
      <Form form={form} layout="vertical" className={styles.ReportForm}>
        {renderTimeFilters()}
        <div className={styles.FilterCard}>
          <Row gutter={[70, 16]}>
            {filterKeys.map((filterKey) => {
              const field = renderFilterField(filterKey);
              if (!field) return null;

              return (
                <Col xs={24} md={12} xl={8} key={filterKey} className={styles.FilterCol}>
                  {field}
                </Col>
              );
            })}
          </Row>
          <div className={styles.ExportRow}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={handleExport}
              disabled={!canExportReport || !activeReport || !isReportAvailable(activeReport)}
              className={styles.ExportButton}
            >
              Export
            </Button>
          </div>
        </div>
      </Form>
    );
  };

  return (
    <PermissionDirect screen={["REPORT_SHEET"]}>
      <div className={styles.Reports}>
      {contextHolder}
      <div className="container">
        <Row gutter={[16, 16]} className={styles.Warp}>
          <Col xs={24} lg={5}>
            <div className={styles.SideBar}>
              <div className={styles.Title}>Báo cáo</div>
              <div className={styles.ListTabs}>
                {reportConfigs.map((report) => {
                  const disabledReport = report.status !== "AVAILABLE";
                  const active = report.code === activeReportCode;

                  return (
                    <button
                      key={report.code}
                      type="button"
                      disabled={disabledReport}
                      className={`${styles.Tab} ${active ? styles.TabActive : ""}`}
                      onClick={() => {
                        if (disabledReport) return;
                        setActiveReportCode(report.code);
                      }}
                    >
                      <span>{report.name}</span>
                      <RightOutlined />
                    </button>
                  );
                })}
              </div>
            </div>
          </Col>
          <Col xs={24} lg={19}>
            <Spin spinning={loadingConfig}>
              <div className={styles.Content}>{renderFilterGrid()}</div>
            </Spin>
          </Col>
        </Row>
      </div>
      </div>
    </PermissionDirect>
  );
};

export { Reports };
